/* ==========================================================================
   Baji Dumplings — theme behaviour
   Cart drawer, quantity steppers, FAQ accordion, mobile nav.
   Cart mutations go through Shopify's /cart AJAX API so the real cart and
   checkout stay authoritative.
   ========================================================================== */

(function () {
  'use strict';

  var body = document.body;
  var routes = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

  /* --------------------------------------------------------- scroll reveal */

  // Elements rise once, on first entry, and are then left alone — re-animating
  // on every scroll past is the thing that makes reveal effects feel cheap.
  var revealTargets = document.querySelectorAll('[data-reveal], [data-reveal-group]');

  if (!('IntersectionObserver' in window)) {
    revealTargets.forEach(function (el) { el.classList.add('is-visible'); });
  } else {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, {
      // Fire slightly before the element reaches the viewport so the motion has
      // finished by the time it is properly in view.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.05
    });

    revealTargets.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------ mobile nav */

  var navToggle = document.querySelector('[data-nav-toggle]');
  var nav = document.querySelector('[data-nav]');
  if (navToggle && nav) {
    navToggle.addEventListener('click', function () {
      var open = nav.getAttribute('data-open') === 'true';
      nav.setAttribute('data-open', open ? 'false' : 'true');
      navToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }

  /* ---------------------------------------------------------- cart drawer */

  var drawer = document.querySelector('[data-cart-drawer]');
  var lastFocused = null;

  function openDrawer() {
    if (!drawer) return;
    lastFocused = document.activeElement;
    body.classList.add('drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    // Must come off before the focus() below, or the target is still unfocusable.
    drawer.removeAttribute('inert');
    var close = drawer.querySelector('[data-drawer-close]');
    if (close) close.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    body.classList.remove('drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
    // Move focus out first: focus sitting inside an element being made inert
    // would otherwise be dropped to <body>.
    if (lastFocused && lastFocused.focus) lastFocused.focus();
    drawer.setAttribute('inert', '');
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openDrawer(); }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-drawer-overlay]')) closeDrawer();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && body.classList.contains('drawer-open')) closeDrawer();
  });

  // Trap focus inside the drawer while it is open.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !body.classList.contains('drawer-open') || !drawer) return;
    var focusables = drawer.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* --------------------------------------------------------- cart refresh */

  // Re-render the drawer and the header count from the live cart. Pulling the
  // section HTML from Shopify keeps money formatting and discounts server-side.
  function refreshCart() {
    return fetch(routes + '?sections=cart-drawer', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var freshBody = parsed.querySelector('[data-drawer-contents]');
        var currentBody = document.querySelector('[data-drawer-contents]');
        if (freshBody && currentBody) currentBody.innerHTML = freshBody.innerHTML;

        var freshCount = parsed.querySelector('[data-cart-count]');
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          if (freshCount) el.textContent = freshCount.textContent;
        });
      })
      .catch(function () { /* leave the drawer as-is; the cart page still works */ });
  }

  /* -------------------------------------------------------- add to cart */

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    var button = form.querySelector('[type="submit"]');
    var original = button ? button.innerHTML : '';
    if (button) { button.setAttribute('disabled', 'disabled'); button.innerHTML = 'Adding…'; }

    fetch(routes + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: Number(form.querySelector('[name="id"]').value),
          quantity: Number((form.querySelector('[name="quantity"]') || { value: 1 }).value)
        }]
      })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.data.description || res.data.message || 'Could not add to cart');
        return refreshCart().then(openDrawer);
      })
      .catch(function (err) {
        var slot = form.querySelector('[data-form-error]');
        if (slot) { slot.textContent = err.message; slot.hidden = false; }
        else window.alert(err.message);
      })
      .finally(function () {
        if (button) { button.removeAttribute('disabled'); button.innerHTML = original; }
      });
  });

  /* --------------------------------------------------- change / remove line */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-line-change]');
    if (!trigger) return;
    e.preventDefault();

    var line = trigger.getAttribute('data-line');
    var quantity = trigger.getAttribute('data-line-change');

    fetch(routes + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    })
      .then(function () {
        // The cart page renders totals server-side, so reload there.
        if (body.classList.contains('template-cart')) { window.location.reload(); return; }
        return refreshCart();
      });
  });

  /* ------------------------------------------------------ quantity stepper */

  document.addEventListener('click', function (e) {
    var step = e.target.closest('[data-qty-step]');
    if (!step) return;
    var input = step.parentNode.querySelector('input');
    if (!input) return;
    var min = Number(input.getAttribute('min') || 1);
    var next = Number(input.value) + Number(step.getAttribute('data-qty-step'));
    input.value = Math.max(min, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------- variant picker */

  function paintPrice(price, compare) {
    var target = document.querySelector('[data-price-target]');
    if (!target) return;
    target.innerHTML = (compare ? '<s>' + compare + '</s> ' : '') + price;
  }

  document.addEventListener('change', function (e) {
    var radio = e.target.closest('[data-variant-radio]');
    if (radio) {
      paintPrice(radio.getAttribute('data-price'), radio.getAttribute('data-compare'));
      // Keep the URL shareable and correct on refresh.
      var url = new URL(window.location.href);
      url.searchParams.set('variant', radio.value);
      window.history.replaceState({}, '', url);
      return;
    }

    var select = e.target.closest('[data-variant-select]');
    if (select) {
      var option = select.options[select.selectedIndex];
      paintPrice(option.getAttribute('data-price'), '');
      var u = new URL(window.location.href);
      u.searchParams.set('variant', select.value);
      window.history.replaceState({}, '', u);
    }
  });

  /* ---------------------------------------------------- product thumbnails */

  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    var hero = document.getElementById('ProductHero');
    if (!hero) return;
    hero.removeAttribute('srcset');
    hero.src = thumb.getAttribute('data-thumb');
    thumb.parentNode.querySelectorAll('[data-thumb]').forEach(function (t) {
      t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
    });
  });

  /* ------------------------------------------------------------------ faq */

  document.addEventListener('click', function (e) {
    var q = e.target.closest('[data-faq-toggle]');
    if (!q) return;
    var item = q.closest('.faq__item');
    var answer = item.querySelector('.faq__a');
    var open = q.getAttribute('aria-expanded') === 'true';

    // One panel at a time, matching the design comp.
    item.parentNode.querySelectorAll('[data-faq-toggle]').forEach(function (other) {
      other.setAttribute('aria-expanded', 'false');
      var panel = other.closest('.faq__item').querySelector('.faq__a');
      if (panel) panel.hidden = true;
      var sign = other.querySelector('.faq__sign');
      if (sign) sign.textContent = '+';
    });

    if (!open) {
      q.setAttribute('aria-expanded', 'true');
      if (answer) answer.hidden = false;
      var ownSign = q.querySelector('.faq__sign');
      if (ownSign) ownSign.textContent = '−';
    }
  });
})();
